import React, { useState } from "react";
import { Modal } from "../common/ui/modal";
import Button from "../common/ui/button/Button";
import { AlertCircle, Mail } from "lucide-react";
import {
  ReminderTiming,
  CreateRequestParticipantDto,
  CreateReminderDto,
} from "@secure-caseworker/shared";

type ReminderOption = {
  id: string;
  label: string;
  timing: ReminderTiming;
  offset_days: number;
};

const REMINDER_OPTIONS: ReminderOption[] = [
  {
    id: "before_3",
    label: "3 days before due date",
    timing: ReminderTiming.BEFORE_DUE,
    offset_days: 3,
  },
  {
    id: "on_due",
    label: "On due date",
    timing: ReminderTiming.ON_DUE,
    offset_days: 0,
  },
  {
    id: "after_3",
    label: "3 days after due date",
    timing: ReminderTiming.AFTER_DUE,
    offset_days: 3,
  },
  {
    id: "after_7",
    label: "7 days after due date",
    timing: ReminderTiming.AFTER_DUE,
    offset_days: 7,
  },
];

export type AddParticipantParams = {
  participant: CreateRequestParticipantDto;
  reminders: CreateReminderDto[];
};

type AddParticipantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (params: AddParticipantParams) => Promise<void>;
  hasDueDate: boolean;
};

export const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  hasDueDate,
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selectedReminders, setSelectedReminders] = useState<Set<string>>(
    new Set(["before_3"]) // Default: 3 days before
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email.trim()) {
      setError("Email is required");
      setLoading(false);
      return;
    }

    try {
      const reminders: CreateReminderDto[] = [];
      if (hasDueDate) {
        for (const reminderId of selectedReminders) {
          const option = REMINDER_OPTIONS.find((o) => o.id === reminderId);
          if (option) {
            reminders.push({
              timing: option.timing,
              offset_days: option.offset_days,
            });
          }
        }
      }

      await onAdd({
        participant: {
          email: email.trim(),
          name: name.trim() || undefined,
        },
        reminders,
      });
      handleClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add participant";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form state after close
    setTimeout(() => {
      setEmail("");
      setName("");
      setSelectedReminders(new Set(["before_3"]));
      setError(null);
    }, 200);
  };

  const toggleReminder = (reminderId: string) => {
    setSelectedReminders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reminderId)) {
        newSet.delete(reminderId);
      } else {
        newSet.add(reminderId);
      }
      return newSet;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Participant"
      maxWidth="480px"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Info Notice */}
        <div className="flex items-start gap-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 border border-blue-200 dark:border-blue-800">
          <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800 dark:text-blue-200">
            An invitation email will be sent immediately when the participant is
            added.
          </p>
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="participant@example.com"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            required
            autoFocus
          />
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Name <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>

        {/* Reminder Cadence */}
        {hasDueDate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Reminder Schedule
            </label>
            <div className="space-y-2">
              {REMINDER_OPTIONS.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedReminders.has(option.id)}
                    onChange={() => toggleReminder(option.id)}
                    className="mr-3 h-4 w-4 text-brand-600 focus:ring-brand-500 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {selectedReminders.size === 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                No reminders will be sent for this participant
              </p>
            )}
          </div>
        )}

        {!hasDueDate && (
          <div className="flex items-start gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700">
            <AlertCircle className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set a due date on the request to enable reminder scheduling.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={loading}>
            {loading ? "Adding..." : "Add Participant"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
